import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Barbell, House, User, UsersThree, Flame, X, CaretLeft, CaretRight,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SLIDES = [
  {
    icon: House,
    title: 'Your Home Base',
    body: 'Every routine lives on the home tab. Tap a card to jump straight into a session — your activity heatmap tracks every minute you spend training.',
  },
  {
    icon: Barbell,
    title: 'Build Any Routine',
    body: 'Head to the Exercises tab or create a new routine to pull from 170+ movements — or roll your own custom lift with the Create Custom button.',
  },
  {
    icon: UsersThree,
    title: 'Bring The Squad',
    body: 'Optional but powerful — create or join a Squad to share routines, react to workouts and compare sessions side by side.',
  },
  {
    icon: Flame,
    title: 'Streaks & Milestones',
    body: 'Train consistently to grow your day-streak. Cross 7, 30 or 100 days to unlock milestone badges — no punishment for rest days, just fuel for the fire.',
  },
  {
    icon: User,
    title: 'Make It Yours',
    body: 'Head to Profile → Settings to tweak your theme, wallpaper, default rest and even reorder the profile sections you care about.',
  },
];

/**
 * First-run onboarding slideshow. Can be opened from Settings later on.
 * onClose is called when the user finishes or dismisses.
 */
export default function TutorialModal({ onClose, source = 'auto' }) {
  const [idx, setIdx] = useState(0);
  const total = SLIDES.length;

  const finish = () => {
    localStorage.removeItem('breth_show_tutorial');
    axios
      .put(`${API_URL}/auth/me`, { tutorial_seen: true }, { withCredentials: true })
      .catch(() => {});
    onClose?.();
  };

  // Hide the persistent nav so the tutorial owns the screen.
  useEffect(() => {
    document.body.classList.add('modal-nav-hidden');
    return () => document.body.classList.remove('modal-nav-hidden');
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(total - 1, i + 1));
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      if (e.key === 'Escape') finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const slide = SLIDES[idx];
  const Icon = slide.icon;
  const isLast = idx === total - 1;

  return (
    <div
      className="fixed inset-0 z-[1200] bg-graphite/60 backdrop-blur-md flex items-center justify-center px-4"
      data-testid={`tutorial-modal-${source}`}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
        <div className="px-5 pt-4 pb-2 flex items-center justify-between border-b border-hairline/60">
          <p className="text-[10px] uppercase tracking-[0.24em] font-black text-graphite/50">
            Getting Started · {idx + 1}/{total}
          </p>
          <button
            onClick={finish}
            className="w-9 h-9 rounded-ios-pill border border-hairline flex items-center justify-center"
            data-testid="tutorial-close"
            aria-label="Skip tutorial"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="px-6 pt-8 pb-6 text-center">
          <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-graphite text-bone flex items-center justify-center shadow-ios">
            <Icon size={40} weight="fill" />
          </div>
          <h2
            className="text-3xl font-black font-heading uppercase text-graphite mb-3 leading-none"
            data-testid={`tutorial-title-${idx}`}
          >
            {slide.title}
          </h2>
          <p className="text-sm text-graphite/70 leading-relaxed">
            {slide.body}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-4">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? 'w-6 bg-userRed' : 'w-1.5 bg-graphite/20'
              }`}
              aria-hidden
            />
          ))}
        </div>

        <div className="px-5 pb-5 flex items-center gap-2">
          <button
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            className="h-12 px-4 rounded-ios-pill border border-hairline text-graphite/70 disabled:opacity-30 flex items-center gap-1.5"
            data-testid="tutorial-prev"
          >
            <CaretLeft size={14} weight="bold" />
            Back
          </button>
          <button
            onClick={() => (isLast ? finish() : setIdx(idx + 1))}
            className="flex-1 h-12 bg-userRed text-bone font-black text-xs uppercase tracking-[0.18em] rounded-ios-pill flex items-center justify-center gap-2"
            data-testid={isLast ? 'tutorial-finish' : 'tutorial-next'}
            data-haptic="success"
          >
            {isLast ? "Let's go" : 'Next'}
            {!isLast && <CaretRight size={14} weight="bold" />}
          </button>
        </div>
      </div>
    </div>
  );
}
